import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, User, MapPin, Heart, Menu, X, ChevronDown, LogOut, Eye, Tag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { useFavorites } from '../context/FavoritesContext';
import api from '../api/client';
import SearchBar from './SearchBar';

const VIEW_AS_OPTIONS = [
  { value: 'off', label: 'My real prices' },
  { value: 'end_user', label: 'End user' },
  { value: 'builder', label: 'Builder' },
  { value: 'retailer', label: 'Retailer' },
];

const Header = () => {
  const navigate = useNavigate();
  const { count } = useCart();
  const { user, logout, viewAsType, setViewAsType } = useAuth();
  const { settings } = useSettings();
  const { count: favCount } = useFavorites();
  const brandName = settings.brand_name || 'Dr Tools';
  const [query, setQuery] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [categories, setCategories] = useState([]);
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpenCat, setMobileOpenCat] = useState(null);

  useEffect(() => {
    api.get('/categories').then(r => setCategories(r.data)).catch(() => {});
  }, []);

  const rootCategories = categories.filter(c => !c.parent_id);
  const subsOf = (id) => categories.filter(c => c.parent_id === id);

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) navigate(`/search?q=${encodeURIComponent(query)}`);
  };

  return (
    <header className="bg-white border-b border-neutral-200 z-40">
      {/* Top strip */}
      <div className="bg-[#12263A] text-white text-xs">
        <div className="max-w-[1400px] mx-auto px-4 py-2 flex items-center justify-between">
          <div className="hidden md:flex items-center gap-4">
            <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Find a store</span>
            <span>Help &amp; advice</span>
            <span>Track order</span>
          </div>
          <div className="flex items-center gap-4 ml-auto">
            <Link
              to="/login?role=retailer"
              data-testid="header-trade-point-link"
              className="hover:text-[#F46A25] transition-colors font-semibold"
            >
              Trade Point
            </Link>
            <span>{brandName} Club</span>
            <span className="hidden sm:inline">Gift cards</span>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className="max-w-[1400px] mx-auto px-4 py-3 flex items-center gap-4">
        <button className="lg:hidden text-[#12263A]" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu">
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>

        <Link to="/" className="flex-shrink-0">
          {settings.logo_url ? (
            <div className="bg-white h-14 md:h-16 flex items-center px-2 rounded-sm">
              <img src={settings.logo_url} alt={brandName} className="h-full w-auto object-contain max-w-[180px]" />
            </div>
          ) : (
            <div className="bg-[#F46A25] text-white font-black text-xl md:text-2xl h-14 md:h-16 px-4 flex items-center justify-center rounded-sm tracking-tight">
              {brandName}
            </div>
          )}
        </Link>

        <SearchBar />

        <div
          className="hidden md:flex items-center gap-1 relative"
          onMouseLeave={() => setUserMenu(false)}
        >
          <button
            onClick={() => user ? setUserMenu(!userMenu) : navigate('/login')}
            className="flex items-center gap-2 px-3 py-2 hover:bg-neutral-100 rounded-sm text-sm text-[#12263A]"
          >
            <User className="w-5 h-5" />
            <div className="text-left leading-tight">
              <div className="text-[11px] text-neutral-600">{user ? 'Hi,' : 'Sign in /'}</div>
              <div className="font-semibold">{user ? user.name : 'Register'}</div>
            </div>
            {user && <ChevronDown className="w-4 h-4" />}
          </button>
          {user && userMenu && (
            <div className="absolute right-0 top-full mt-1 bg-white border border-neutral-200 shadow-lg rounded-sm min-w-[200px] z-50">
              <div className="px-4 py-2 border-b border-neutral-100">
                <div className="text-xs text-neutral-500">Signed in as</div>
                <div className="text-sm font-semibold text-[#12263A] truncate">{user.email}</div>
                <div className="text-[10px] uppercase mt-1 inline-block bg-orange-100 text-[#F46A25] font-bold px-1.5 py-0.5 rounded-sm">
                  {user.user_type === 'builder' ? 'Trade' : user.user_type === 'retailer' ? 'Retailer' : 'Personal'}
                </div>
              </div>
              <Link to="/account" className="block px-4 py-2 text-sm hover:bg-neutral-100" onClick={() => setUserMenu(false)}>My account</Link>
              <Link to="/orders" className="block px-4 py-2 text-sm hover:bg-neutral-100" onClick={() => setUserMenu(false)}>My orders</Link>
              {user.is_admin && (
                <Link to="/admin" className="block px-4 py-2 text-sm hover:bg-neutral-100 text-[#F46A25] font-semibold border-t border-neutral-100" onClick={() => setUserMenu(false)}>Admin panel</Link>
              )}
              <button onClick={() => { logout(); setUserMenu(false); navigate('/'); }} className="w-full text-left px-4 py-2 text-sm hover:bg-neutral-100 flex items-center gap-2 border-t border-neutral-100">
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>
          )}
        </div>

        <Link
          to="/favorites"
          data-testid="header-lists-link"
          className="hidden md:flex items-center gap-2 px-3 py-2 hover:bg-neutral-100 rounded-sm text-sm text-[#12263A] relative"
        >
          <div className="relative">
            <Heart className="w-5 h-5" />
            {favCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#F46A25] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{favCount}</span>
            )}
          </div>
          <span className="font-semibold">Wishlist</span>
        </Link>

        <Link to="/cart" className="flex items-center gap-2 px-3 py-2 hover:bg-neutral-100 rounded-sm text-sm text-[#12263A] relative">
          <div className="relative">
            <ShoppingCart className="w-6 h-6" />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#F46A25] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{count}</span>
            )}
          </div>
          <span className="hidden md:inline font-semibold">Basket</span>
        </Link>
      </div>

      {/* Admin-only "View as" preview bar */}
      {user?.is_admin && (
        <div className="bg-[#F46A25] text-white text-xs" data-testid="admin-view-as-bar">
          <div className="max-w-[1400px] mx-auto px-4 py-1.5 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Eye className="w-3.5 h-3.5" />
              <span className="font-semibold uppercase tracking-wider text-[10px]">Admin preview</span>
              <span className="hidden md:inline text-white/80">
                {viewAsType
                  ? <>Currently browsing storefront with <b>{VIEW_AS_OPTIONS.find(o => o.value === viewAsType)?.label}</b> pricing.</>
                  : 'Preview storefront pricing as any customer tier.'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="view-as-select" className="uppercase text-[10px] font-bold tracking-wider">View as:</label>
              <select
                id="view-as-select"
                data-testid="view-as-select"
                value={viewAsType || 'off'}
                onChange={(e) => setViewAsType(e.target.value)}
                className="bg-white/95 text-[#12263A] font-bold text-xs px-2 py-1 rounded-sm focus:outline-none focus:ring-2 focus:ring-white"
              >
                {VIEW_AS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {viewAsType && (
                <button
                  data-testid="view-as-clear"
                  onClick={() => setViewAsType(null)}
                  className="bg-white/10 hover:bg-white/25 text-white font-bold uppercase text-[10px] px-2 py-1 rounded-sm border border-white/30"
                  title="Return to my real prices"
                >
                  Reset
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Category nav */}
      <nav className="bg-[#12263A] text-white hidden lg:block relative">
        <div className="max-w-[1400px] mx-auto px-2 flex flex-nowrap items-stretch">
          {rootCategories.map((cat) => {
            const subs = subsOf(cat.id);
            const hasSubs = subs.length > 0;
            return (
              <div
                key={cat.id}
                className="relative flex flex-1 min-w-0"
                onMouseEnter={() => hasSubs && setOpenMenu(cat.id)}
                onMouseLeave={() => setOpenMenu(null)}
              >
                <Link
                  to={`/category/${cat.slug}`}
                  className={`w-full px-2 py-2 text-[13px] leading-tight flex items-center justify-center gap-1 transition-colors border-r border-white/10 text-center whitespace-normal ${openMenu === cat.id ? 'bg-[#F46A25]' : 'hover:bg-[#F46A25]'}`}
                  onClick={() => setOpenMenu(null)}
                >
                  <span className="line-clamp-2">{cat.name}</span>
                  {hasSubs && <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" />}
                </Link>
                {hasSubs && openMenu === cat.id && (
                  <div className="absolute left-0 top-full bg-white text-[#12263A] shadow-2xl border border-neutral-200 min-w-[240px] z-50 rounded-b-sm">
                    <Link to={`/category/${cat.slug}`} className="block px-4 py-2.5 text-sm font-bold text-[#F46A25] hover:bg-orange-50 border-b border-neutral-100" onClick={() => setOpenMenu(null)}>
                      Shop all {cat.name} →
                    </Link>
                    {subs.map(s => (
                      <Link
                        key={s.id}
                        to={`/category/${s.slug}`}
                        onClick={() => setOpenMenu(null)}
                        className="block px-4 py-2 text-sm hover:bg-orange-50 hover:text-[#F46A25]"
                      >
                        {s.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {/* Help & Advice — editorial hub */}
          <Link
            to="/help-advice"
            data-testid="header-help-advice"
            className="px-3 py-2 text-[13px] leading-tight flex items-center justify-center hover:bg-[#F46A25] font-semibold whitespace-nowrap flex-shrink-0"
          >
            Help &amp; Advice
          </Link>
          {/* Automated Sale category — always at the end, styled red */}
          <Link
            to="/category/sale"
            data-testid="header-sale-tab"
            className="px-3 py-2 text-[13px] leading-tight flex items-center justify-center gap-1 bg-red-600 hover:bg-red-700 text-white font-bold uppercase transition-colors flex-shrink-0"
          >
            <Tag className="w-3.5 h-3.5" /> Sale
          </Link>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <nav className="lg:hidden bg-[#12263A] text-white max-h-[70vh] overflow-y-auto" data-testid="mobile-menu">
          {rootCategories.map((cat) => {
            const subs = subsOf(cat.id);
            const hasSubs = subs.length > 0;
            const isOpen = mobileOpenCat === cat.id;
            return (
              <div key={cat.id} className="border-b border-white/10">
                {hasSubs ? (
                  <button
                    type="button"
                    data-testid={`mobile-cat-toggle-${cat.slug}`}
                    onClick={() => setMobileOpenCat(isOpen ? null : cat.id)}
                    className={`w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-left ${isOpen ? 'bg-[#F46A25]' : 'hover:bg-[#F46A25]'}`}
                    aria-expanded={isOpen}
                  >
                    <span>{cat.name}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                ) : (
                  <Link
                    to={`/category/${cat.slug}`}
                    onClick={() => { setMobileOpen(false); setMobileOpenCat(null); }}
                    className="block px-4 py-3 text-sm font-semibold hover:bg-[#F46A25]"
                  >
                    {cat.name}
                  </Link>
                )}
                {hasSubs && isOpen && (
                  <div className="bg-[#0e1f30]" data-testid={`mobile-cat-subs-${cat.slug}`}>
                    <Link
                      to={`/category/${cat.slug}`}
                      onClick={() => { setMobileOpen(false); setMobileOpenCat(null); }}
                      className="block pl-8 pr-4 py-2 text-xs font-bold text-[#F46A25] hover:bg-[#F46A25] hover:text-white"
                    >
                      Shop all {cat.name} →
                    </Link>
                    {subs.map(s => (
                      <Link
                        key={s.id}
                        to={`/category/${s.slug}`}
                        onClick={() => { setMobileOpen(false); setMobileOpenCat(null); }}
                        className="block pl-8 pr-4 py-2 text-xs text-white/80 hover:bg-[#F46A25] hover:text-white"
                      >
                        — {s.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {!user && (
            <Link to="/login" onClick={() => setMobileOpen(false)} className="block px-4 py-3 text-sm bg-[#F46A25] font-semibold">Sign in / Register</Link>
          )}
          <Link
            to="/category/sale"
            onClick={() => { setMobileOpen(false); setMobileOpenCat(null); }}
            data-testid="mobile-sale-link"
            className="block px-4 py-3 text-sm bg-red-600 text-white font-bold uppercase"
          >
            🏷️ Sale
          </Link>
        </nav>
      )}
    </header>
  );
};

export default Header;
